import {
  DataCard,
  pipeValueFormatters,
  withDollarFormat,
  withHyphenIfEmptyValueFormatter,
} from '@fluentsms/agentnet-web-components';
import { useTranslation } from '@fluentsms/i18n';
import { Box, Button, Divider, IconButton, Tooltip } from '@material-ui/core';
import { SettingsOutlined } from '@material-ui/icons';
import {
  ColDef,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FilterModel,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  MenuItemDef,
  ValueFormatterFunc,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { AgGridReactProps } from 'ag-grid-react';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import React, {
  ComponentType,
  FC,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GridDefaultDimensions } from '../..';
import { useLocalStorage } from '../../LocalStorage';
import {
  ClientSideDataGridExport,
  ClientSideDataGridSearch,
  DataGrableHeaderMenuActions,
  DataGrableHeaderMenuActionsProps,
  DataGrableLoading,
  DataGrableRowAction,
  DataGrableRowActions,
  EmptySetOverlay,
  EmptySetOverlayProps,
} from '../components';
import { ServerSideDataGridSearch } from '../components/ServerSideDataGridSearch';

export interface DataGrablePersistenceSettings {
  namespace: string;
  gridId: string;
  userId?: string;
  persistColumnWidths?: boolean;
}

export interface PersistedDataGrable {
  columns: ColumnState[];
  columnsWithChangedWidths: string[];
  rowGroups: string[];
  filters: Record<string, string>;
}

export type withFluentStylesProps = {
  className?: string;
  title?: string;
  subheader?: string;
  gridConfig: AgGridReactProps;
  headerActions?: ReactNode[];
  includeSettingsHeaderAction?: boolean;
  includeExportHeaderAction?: boolean;
  includeMenuHeaderAction?: boolean;
  includeTableFilters?: boolean;
  includeSearch?: boolean;
  includeFilters?: boolean;
  isPrintable?: boolean;
  isInDrawer?: boolean;
  menuHeaderActions?: DataGrableHeaderMenuActionsProps;
  enableRowGrouping?: boolean;
  contextMenuActions?: (params: GetContextMenuItemsParams) => (string | MenuItemDef)[];
  expandOrCollapseAllMenuActions?: { contextMenuActions: boolean; headerMenuActions: boolean };
  includeDefaultContextMenuActions?: boolean;
  excludeDefaultContextMenuActions: string[];
  rowActions?: DataGrableRowAction<any>[]; // TODO: When restructuring to specify types, handle this as well.
  loading?: boolean;
  persistence?: DataGrablePersistenceSettings;
  onRowClicked?(row: any): void;
  footerComponent?: ReactNode;
  gridHeight?: number | 'auto';
  gridMinHeight?: number;
  emptySetProps: EmptySetOverlayProps;
  EmptySetComponent?: FC<EmptySetOverlayProps>;
  getApiReference?: (gridApi: GridApi) => unknown;
  gridDefaultDimensions: GridDefaultDimensions;
  enableColumnVisibilityChange?: boolean;
  openInNewTabPath?: string;
  onFilterChanged?: (e: FilterChangedEvent<any, any>) => void;
  onGridReady?: (e: GridReadyEvent<any, any>) => void;
  onColumnVisible?: (e: ColumnVisibleEvent<any, any>) => void;
  columnSettingsVerticalOffsetModifier?: number;
  overrideClasses?: Record<string, string>;
  enableDummyColumn?: boolean;
};

export type withClientSideGridOutput = {
  title: ReactNode;
  agGridProps?: AgGridReactProps;
};

export const ROW_ACTION_COLUMN_WIDTH = 50;

export const AG_GRID_MAIN_WRAPPER_CLASSNAME = '.ag-root-wrapper';
export const AG_GRID_CENTER_CONTAINER_CLASSNAME = '.ag-center-cols-container';
export const AG_GRID_PINNED_LEFT_HEADER = '.ag-pinned-left-header';

export const DEFAULT_LOADING_SKELETON_COLUMNS = 5;
export const DEFAULT_LOADING_SKELETON_ROWS = 5;
export const DEFAULT_ROWS_COUNT = 10;

type GridStateSavingEvent =
  | 'filterChanged'
  | 'sortChanged'
  | 'columnRowGroupChanged'
  | 'columnPivotChanged'
  | 'columnValueChanged'
  | 'columnVisible'
  | 'columnPinned'
  | 'columnResized'
  | 'columnMoved'
  | 'rowDragCancel';

export const UPDATING_PERSISTED_STATE_DEBOUNCE_RATE = 250;
export const GRID_STATE_SAVING_EVENTS: GridStateSavingEvent[] = [
  'filterChanged',
  'sortChanged',
  'columnRowGroupChanged',
  'columnPivotChanged',
  'columnValueChanged',
  'columnVisible',
  'columnPinned',
  'columnResized',
  'columnMoved',
  'rowDragCancel',
];

export const TIME_TO_AVOID_HEADER_ANIMATION = 150;

export const GRID_DEFAULT_HEIGHT = 400;
export const GRID_CONTROLS_HEIGHT = 42;
export const GRID_FILTER_CONTROLS_HEIGHT = 40;
export const GRID_ROW_HEIGHT = 40;
export const GRID_ROW_GROUP_HEIGHT = 40;
export const WINDOWS_SCROLLBAR_HEIGHT = 17;
export const BOTTOM_OF_SCREEN_PADDING_NO_FOOTER = 120;
export const BOTTOM_OF_SCREEN_PADDING_WITH_FOOTER = 160;
export const GRID_RESIZE_THROTTLE_TIMER = 75;
export const ACTIONS_COLUMN_NAME = '<ACTIONS>';
export const DUMMY_COLUMN_NAME = '<DUMMY>';
export const DRAWER_FOOTER_HEIGHT = 34;

const EXPAND_ALL = 'Expand All';
const COLLAPSE_ALL = 'Collapse All';

//NOTE: This would be used to remove fluent specific concerns from the base DataGrable
// and handle them in this enhancer with ability to pass refs and other props to the base component
export function withFluentEnhancers(WrappedComponent: ComponentType<any>) {
  function EnhancedComponent(props: withFluentStylesProps) {
    const {
      className,
      title,
      subheader,
      headerActions = [],
      includeSettingsHeaderAction = true,
      contextMenuActions,
      includeMenuHeaderAction = false,
      includeTableFilters = false,
      menuHeaderActions = { actions: [] },
      expandOrCollapseAllMenuActions = { contextMenuActions: true, headerMenuActions: true },
      includeDefaultContextMenuActions = true,
      excludeDefaultContextMenuActions = [],
      enableColumnVisibilityChange = true,
      includeExportHeaderAction = true,
      includeSearch = true,
      includeFilters = true,
      isPrintable = false,
      isInDrawer = false,
      enableRowGrouping = false,
      rowActions = [],
      gridDefaultDimensions,
      gridConfig,
      gridHeight = 'auto',
      persistence = null,
      loading = false,
      onRowClicked = undefined,
      footerComponent: FooterComponent,
      gridMinHeight,
      emptySetProps,
      EmptySetComponent,
      openInNewTabPath = '',
      getApiReference,
      columnSettingsVerticalOffsetModifier = 0,
      enableDummyColumn = false,
      ...rest
    } = props;
    const { t } = useTranslation('common');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCount, setFilterCount] = useState<number>(0);
    const agGridApi = useRef<GridApi>(null);
    const { persistColumnWidths } = persistence ?? { persistColumnWidths: true };
    const rowActionsComponent = useCallback(
      (params: ICellRendererParams) =>
        params.data ? <DataGrableRowActions row={params.data} actions={rowActions} disabled={loading} /> : null,
      [rowActions, loading],
    );
    const emptyComponent = useCallback(() => <></>, []);
    const { get, set } = useLocalStorage();
    const [loadedFromPersistence, setLoadedFromPersistence] = useState(!persistence);
    const calculatedGridHeightRef = useRef(gridHeight === 'auto' ? GRID_DEFAULT_HEIGHT : gridHeight);
    const [needsDummyColumn, setNeedsDummyColumn] = useState(false);
    const [columnsWithChangedWidths, setColumnsWithChangedWidths] = useState([] as string[]);
    const iconButtonRef = useRef<HTMLButtonElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const isServerSideGrid = gridConfig.rowModelType === 'serverSide';

    const openOrderInNewTab = useCallback((orderNumber: string) => {
      const copyOrOpenOrder = [
        'separator',
        {
          name: t('dataGrid.copyOrderLink'),
          action: () => {
            navigator.clipboard.writeText(`${window.location.origin}/orders/${orderNumber}${openInNewTabPath}`);
          },
        },
        {
          name: t('dataGrid.openOrderLinkInNewTab'),
          action: () => {
            orderNumber && window.open(`/orders/${orderNumber}${openInNewTabPath}`, '_blank');
          },
        },
      ];
      return copyOrOpenOrder;
    }, []);

    const expandAll = useCallback(() => {
      if (agGridApi.current) agGridApi.current.expandAll();
    }, []);

    const collapseAll = useCallback(() => {
      if (agGridApi.current) agGridApi.current.collapseAll();
    }, []);

    const expandAllOrCollapseAll = useMemo(
      () => [
        'separator',
        {
          name: EXPAND_ALL,
          action: expandAll,
        },
        {
          name: COLLAPSE_ALL,
          action: collapseAll,
        },
      ],
      [expandAll, collapseAll],
    );

    const adjustGridHeight = throttle(() => {
      const wrapperParent = wrapperRef.current;

      if (wrapperParent) {
        const { height: wrapperParentHeight, bottom: wrapperParentBottom } = wrapperParent.getBoundingClientRect();
        const remainingSpace = window.innerHeight - wrapperParentBottom - (isInDrawer ? DRAWER_FOOTER_HEIGHT : 0); // How much space is between the bottom of the grid and the bottom of the screen?
        const bottomOfScreenPadding = FooterComponent
          ? BOTTOM_OF_SCREEN_PADDING_WITH_FOOTER
          : BOTTOM_OF_SCREEN_PADDING_NO_FOOTER;
        const potentialFullSize = remainingSpace + wrapperParentHeight - bottomOfScreenPadding; // If there grid were allowed to grow, how big could it be and fit on the screen?
        const maxAllowedSize = gridHeight === 'auto' ? potentialFullSize : gridHeight;
        const rowCount = loading
          ? DEFAULT_ROWS_COUNT
          : agGridApi.current?.getDisplayedRowCount() ?? fullGridConfig.rowData?.length ?? 0;
        const isWindowsMachine = navigator?.userAgent?.includes?.('Windows');
        const heightFromRows =
          rowCount * GRID_ROW_HEIGHT +
          GRID_CONTROLS_HEIGHT +
          (enableRowGrouping ? GRID_ROW_GROUP_HEIGHT : 0) +
          (includeFilters ? GRID_FILTER_CONTROLS_HEIGHT : 0) +
          (isWindowsMachine ? WINDOWS_SCROLLBAR_HEIGHT : 0); // If the grid were sized based only on the number of rows, how big would it be?
        let calculatedHeight = Math.min(maxAllowedSize, heightFromRows + 1);
        const minAllowedSize = gridMinHeight ?? GRID_ROW_HEIGHT + GRID_CONTROLS_HEIGHT;

        calculatedHeight = Math.max(minAllowedSize, calculatedHeight);
        if (rowCount > 0) {
          calculatedGridHeightRef.current = calculatedHeight;

          // Dynamically update the grid height without triggering a re-render
          if (wrapperRef.current) {
            const firstChild = wrapperRef.current?.lastChild as HTMLElement | null;
            const secondChild = firstChild?.firstChild as HTMLElement | null;
            const lastChild = secondChild?.firstChild as HTMLElement | null;
            if (lastChild) {
              lastChild.style.height = `${calculatedGridHeightRef.current}px`;
            }
          }
        }
      }
    }, GRID_RESIZE_THROTTLE_TIMER);

    const fullGridConfig = useMemo(() => {
      const config = cloneDeep(gridConfig);

      config.loadingOverlayComponent = DataGrableLoading;

      config.defaultColDef = {
        resizable: true,
        minWidth: ROW_ACTION_COLUMN_WIDTH,
        enableRowGroup: true,
        floatingFilter: !isPrintable && includeFilters,
        sortable: true,
      };

      // Prevent "custom prop overrides" from not working if gridOptions isn't defined.
      if (!config.gridOptions) {
        config.gridOptions = {};
      }

      // Provide a common selection of selectable types.
      config.columnTypes = {
        ...config.columnTypes,
        currency: {
          cellStyle: {
            textAlign: 'right',
          },
          filter: 'agNumberColumnFilter',
          valueFormatter: (column) => {
            if (column.node?.group && column.column.getAggFunc() === 'count') {
              return column.value; // Don't format group headers for "count," but do format the rest. [1, 2, 3] doesn't have $3.00 entries.
            } else {
              return pipeValueFormatters(withDollarFormat, withHyphenIfEmptyValueFormatter)(column);
            }
          },
        },
      };

      // Dragging a column outside of the grid shouldn't hide it.
      config.gridOptions.suppressDragLeaveHidesColumns = true;

      if (enableRowGrouping) {
        config.gridOptions.rowGroupPanelShow = isPrintable ? 'never' : 'always';
        config.gridOptions.groupDefaultExpanded = -1;
        config.gridOptions.onColumnRowGroupChanged = (e) => {
          props.gridConfig.gridOptions?.onColumnRowGroupChanged?.(e);
        };
      }

      if (isPrintable) {
        config.gridOptions.domLayout = 'print';
        config.gridOptions.floatingFiltersHeight = 0;
        config.columnDefs = config.columnDefs?.filter((col) => (col as any).type !== 'rowActions');
      }

      // Row Actions
      if (rowActions.length > 0 && config.columnDefs && !isPrintable) {
        // Add the "Row Actions" column
        config.columnDefs.push({
          colId: ACTIONS_COLUMN_NAME,
          headerName: '',
          field: 'actions',
          pinned: 'right',
          lockPinned: true,
          lockPosition: 'right',
          filter: false,
          sortable: false,
          editable: false,
          resizable: false,
          enableRowGroup: false,
          suppressColumnsToolPanel: true,
          suppressNavigable: true,
          suppressFiltersToolPanel: true,
          suppressSizeToFit: true,
          suppressMovable: true,
          menuTabs: [],
          width: ROW_ACTION_COLUMN_WIDTH,
          suppressHeaderMenuButton: true,
          cellRenderer: rowActionsComponent,
        });
      }

      // Add an extra empty column at the end that stretches the remaining width.
      if (needsDummyColumn) {
        config.columnDefs?.push({
          colId: DUMMY_COLUMN_NAME,
          headerName: '',
          field: '',
          filter: false,
          sortable: false,
          editable: false,
          resizable: false,
          enableRowGroup: false,
          suppressAutoSize: true,
          suppressColumnsToolPanel: true,
          suppressNavigable: true,
          suppressFiltersToolPanel: true,
          suppressSizeToFit: true,
          suppressMovable: true,
          menuTabs: [],
          suppressHeaderMenuButton: true,
          cellRenderer: emptyComponent,
          flex: 1,
        });
      }

      // Context Menu
      config.getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
        const defaultItems = params?.defaultItems?.filter((item) => !excludeDefaultContextMenuActions.includes(item));
        let result: (string | MenuItemDef)[] = includeDefaultContextMenuActions ? [...(defaultItems || [])] : [];

        if (contextMenuActions) {
          result = [...result, ...contextMenuActions(params)];
        }

        const orderNumber = params?.node?.field === 'orderNumber' ? params?.node?.key : params?.node?.data?.orderNumber;
        if (orderNumber) {
          result = [...result, ...openOrderInNewTab(orderNumber)];
        }

        return result;
      };

      for (const column of config.columnDefs ?? []) {
        const asColumn = column as ColDef<any>;

        // When the consumer enables row grouping and a given column hasn't specified that it is not groupable, enable it automatically.
        if (enableRowGrouping && typeof asColumn.enableRowGroup === 'undefined') {
          asColumn.enableRowGroup = true;
        }
      }

      // Size grid to fit when user toggles column visibility
      config.onColumnVisible = (params) => {
        if (enableColumnVisibilityChange) {
          params.api.autoSizeColumns([DUMMY_COLUMN_NAME], true);
          params.api.sizeColumnsToFit();
        }

        // Since `onColumnVisible` is globally defined, it invalidates the grid config passed
        // We pass `onColumnVisible` as a prop instead.
        // TODO: Can we remove all the dummy columns
        props.onColumnVisible?.(params);
      };

      // In the column menu, a few items are different than the default implementation because of the way persistence works.
      config.getMainMenuItems = (params) => {
        const columnType = params.column.getColDef().type;

        const someColumnsAreGrouped =
          gridConfig.gridOptions?.treeData || (agGridApi.current?.getRowGroupColumns().length ?? 0 > 0);

        const customAutosizeThis = {
          name: 'Autosize This Column',
          action: () => {
            const autosizedColumn = params.column.getColId();

            agGridApi.current?.autoSizeColumns([autosizedColumn]);
            setColumnsWithChangedWidths((prev) => prev.filter((each) => each !== autosizedColumn));
          },
        };
        const customAutosizeAll = {
          name: 'Autosize All Columns',
          action: () => {
            const columns = params.api.getAllGridColumns();
            const definedColumns = columns.filter(
              (column) => ![ACTIONS_COLUMN_NAME, DUMMY_COLUMN_NAME].includes(column.getColId()),
            );

            agGridApi.current?.autoSizeColumns(definedColumns);
            setColumnsWithChangedWidths([]);
          },
        };
        const sizeColumnsToFit = {
          name: 'Size Columns to Fit',
          action: () => {
            agGridApi.current?.sizeColumnsToFit();
            setColumnsWithChangedWidths([]);
          },
        };
        const expandOrCollapseMenu =
          expandOrCollapseAllMenuActions.headerMenuActions && someColumnsAreGrouped ? expandAllOrCollapseAll : [];

        let aggSubMenu = '';

        // The context menu from AgGrid is clobbered and ignores `enableValue` on ColDef
        // which would apply the aggregation menu. Only allowing number and currenty `type`
        // which is manually set on the ColDef.
        if (someColumnsAreGrouped && (columnType === 'number' || columnType === 'currency')) {
          aggSubMenu = 'valueAggSubMenu';
        }

        return [
          'pinSubMenu',
          aggSubMenu,
          'separator',
          customAutosizeThis,
          customAutosizeAll,
          sizeColumnsToFit,
          'separator',
          'rowGroup',
          'separator',
          'resetColumns',
          ...expandOrCollapseMenu,
        ].filter(Boolean);
      };

      return config;
    }, [
      gridConfig,
      isPrintable,
      includeFilters,
      enableRowGrouping,
      rowActions.length,
      needsDummyColumn,
      rowActionsComponent,
      emptyComponent,
      includeDefaultContextMenuActions,
      contextMenuActions,
      excludeDefaultContextMenuActions,
      openOrderInNewTab,
      enableColumnVisibilityChange,
      expandOrCollapseAllMenuActions.headerMenuActions,
      expandAllOrCollapseAll,
      adjustGridHeight,
      props.gridConfig.gridOptions,
    ]);

    const inEmptyState = !loading && fullGridConfig.rowData?.length === 0;

    const handleFilterChanged = useCallback(() => {
      if (!isServerSideGrid) {
        agGridApi.current?.setGridOption('quickFilterText', searchQuery);
      }
    }, [searchQuery]);

    const handleServerSideQuickFilterChange = useCallback((value: string) => {
      if (agGridApi.current) {
        agGridApi.current.setGridOption('quickFilterText', value);
        agGridApi.current.refreshServerSide({ purge: true });
      }
    }, []);

    const handleCloseToolPanel = useCallback((event: MouseEvent) => {
      const clickedElement = event.target as Element;
      const clickedInToolPanel = Boolean(clickedElement.closest('.ag-tool-panel-wrapper'));

      if (!clickedInToolPanel) {
        // The user has clicked on an element that is not within the tool panel; close the tool panel.
        agGridApi.current?.closeToolPanel();
        document.removeEventListener('click', handleCloseToolPanel);
      }
    }, []);

    const handleAlignToolPanelToSettingsButton = useCallback(() => {
      const toolPanelWrapper = document.querySelector('.ag-tool-panel-wrapper') as HTMLElement | null;

      if (toolPanelWrapper && iconButtonRef.current) {
        const dimensions = iconButtonRef.current.getBoundingClientRect();
        const left = dimensions.left + dimensions.width / 2;
        const top = dimensions.top + dimensions.height / 2;

        toolPanelWrapper.style.position = 'fixed';
        toolPanelWrapper.style.top = `${top - columnSettingsVerticalOffsetModifier}px`;

        // Check if the toolPanelWrapper would be outside the window's right edge
        const toolPanelWidth = toolPanelWrapper.offsetWidth;
        const windowWidth = window.innerWidth;

        if (left + toolPanelWidth > windowWidth) {
          // Position to the left of the iconButtonRef
          toolPanelWrapper.style.left = `${left - toolPanelWidth}px`;
        } else {
          // Position to the right of the iconButtonRef
          toolPanelWrapper.style.left = `${left}px`;
        }
      }
    }, [columnSettingsVerticalOffsetModifier]);

    const handleColumnSettings = useCallback(() => {
      if (agGridApi.current) {
        // Display the column settings panel, then set up an event to handle closing it.
        agGridApi.current.setGridOption('sideBar', {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                suppressPivotMode: true,
              },
            },
          ],
        });
        agGridApi.current.openToolPanel('columns');

        handleAlignToolPanelToSettingsButton();

        document.addEventListener('click', handleCloseToolPanel);
        window.addEventListener('scroll', handleAlignToolPanelToSettingsButton);
        window.addEventListener('resize', handleAlignToolPanelToSettingsButton);
      }
    }, [handleCloseToolPanel, handleAlignToolPanelToSettingsButton]);

    const handleFilterSettings = useCallback(() => {
      window.removeEventListener('scroll', handleAlignToolPanelToSettingsButton);
      window.removeEventListener('resize', handleAlignToolPanelToSettingsButton);

      if (agGridApi.current) {
        // Display the column settings panel, then set up an event to handle closing it.
        agGridApi.current.setGridOption('sideBar', {
          toolPanels: [
            {
              id: 'filters',
              labelDefault: 'filters',
              labelKey: 'filters',
              iconKey: 'filters',
              toolPanel: 'agFiltersToolPanel',
              toolPanelParams: {
                suppressFilterSearch: true,
              },
              width: 300,
            },
          ],
        });
        agGridApi.current.openToolPanel('filters');

        document.addEventListener('click', handleCloseToolPanel);
      }
    }, [handleCloseToolPanel]);

    function exportToXlsx() {
      if (agGridApi.current) {
        agGridApi.current.exportDataAsExcel({
          autoConvertFormulas: true,
          processCellCallback: (params) => {
            const colDef = params.column.getColDef();

            if (colDef.cellRendererParams?.linkTemplate) {
              const cellVal = params.value;
              const isTreeData = gridConfig.gridOptions?.treeData;
              const matchSpaces = /\s/g;
              if (!cellVal) {
                return '';
              } else if (isTreeData && cellVal.includes('->')) {
                const link =
                  location.origin +
                  colDef.cellRendererParams.linkTemplate
                    .replace(':value', cellVal.replace('->', ''))
                    .replace(matchSpaces, '');
                return `=HYPERLINK("${link}", "${cellVal}")`;
              } else if (!isTreeData) {
                const link = location.origin + colDef.cellRendererParams.linkTemplate.replace(':value', cellVal);
                return `=HYPERLINK("${link}", "${cellVal}")`;
              }
            }

            // try to reuse valueFormatter from the colDef
            if (colDef.valueFormatter) {
              const valueFormatterParams: ValueFormatterParams = {
                ...params,
                data: params.node?.data,
                node: params.node as IRowNode,
                colDef: params.column.getColDef(),
              };
              const formattedValue = (colDef.valueFormatter as ValueFormatterFunc)(valueFormatterParams);
              if (String(formattedValue).includes('$')) {
                return `=DOLLAR(${params.value}, 2)`;
              } else return formattedValue;
            }
            return params.value;
          },
        });
      }
    }

    // Once the grid is ready, retrieve the persisted state from storage and then allow the grid to render.
    const hasInitializedFromPersistence = useRef(false);
    const loadPersistedState = useCallback(() => {
      if (persistence && !hasInitializedFromPersistence.current && agGridApi.current) {
        const persistenceIdentifier = formatPersistenceIdentifier(persistence);
        const persistedState = get<PersistedDataGrable>(persistenceIdentifier);

        if (persistedState) {
          const {
            columns = [],
            columnsWithChangedWidths: savedColumnsWithChangedWidths = [],
            rowGroups = [],
            filters,
          } = persistedState;
          const columnsWithAppliedWidths = columns.map((each) => {
            if (each.colId !== ACTIONS_COLUMN_NAME && !savedColumnsWithChangedWidths.includes(each.colId)) {
              delete each.width;
              each.flex = 1;
            }

            return each;
          });

          agGridApi.current.applyColumnState({
            state: columnsWithAppliedWidths,
            applyOrder: true,
          });

          if (persistColumnWidths) {
            setColumnsWithChangedWidths(savedColumnsWithChangedWidths);
          } else {
            setColumnsWithChangedWidths([]);
            agGridApi.current.sizeColumnsToFit();
          }

          agGridApi.current.setRowGroupColumns(rowGroups);
          agGridApi.current.setFilterModel(filters);
        } else {
          agGridApi.current.sizeColumnsToFit();
        }

        // Avoid the "transitional animations" as the default column configuration snaps to the stored one.
        setTimeout(() => setLoadedFromPersistence(true), TIME_TO_AVOID_HEADER_ANIMATION);

        hasInitializedFromPersistence.current = true;
      }
    }, [persistence, get, persistColumnWidths]);

    // Once the grid has initially loaded and any persisted state has been applied,
    // listen to each of the events outlined above and when they fire, updated the persisted state for future loads.
    useEffect(() => {
      if (persistence && hasInitializedFromPersistence.current && agGridApi.current) {
        const persistenceIdentifier = formatPersistenceIdentifier(persistence);
        const gridApi = agGridApi.current;
        const handler = debounce((event: any) => {
          let actualColumnsWithChangedWidths = columnsWithChangedWidths;

          if (event.type === 'columnResized' && persistColumnWidths) {
            const columns = event.columns ?? [];
            const resizedColumnIds = columns.map((column: any) => column.colId);
            const nextColumnsWithChangedWidths =
              event.source === 'autosizeColumns'
                ? columnsWithChangedWidths.filter((each) => !resizedColumnIds.includes(each)) // When autosizing, remove "changed width" status
                : Array.from(new Set(columnsWithChangedWidths.concat(resizedColumnIds)));

            setColumnsWithChangedWidths(nextColumnsWithChangedWidths);
            actualColumnsWithChangedWidths = nextColumnsWithChangedWidths;
          }

          const persistedState = {
            columns: agGridApi.current?.getColumnState() || [],
            columnsWithChangedWidths: actualColumnsWithChangedWidths,
            rowGroups: agGridApi.current?.getRowGroupColumns()?.map((rowGroup) => rowGroup.getColId()),
            filters: gridApi.getFilterModel(),
          };

          set(persistenceIdentifier, persistedState);
        }, UPDATING_PERSISTED_STATE_DEBOUNCE_RATE);

        for (const stateSavingEvent of GRID_STATE_SAVING_EVENTS) {
          gridApi.addEventListener(stateSavingEvent, handler as any);
        }

        return () => {
          for (const stateSavingEvent of GRID_STATE_SAVING_EVENTS) {
            gridApi.removeEventListener(stateSavingEvent, handler as any);
          }
        };
      }
    });

    // Automatically adjust the height of the grid when the user resizes the browser window.
    useEffect(() => {
      window.addEventListener('resize', adjustGridHeight);
      adjustGridHeight();

      return () => {
        window.removeEventListener('resize', adjustGridHeight);
      };
    });

    useEffect(() => {
      handleFilterChanged();
    }, [searchQuery, handleFilterChanged]);

    // In case the user navigates away from the tab without first closing the panel, clean it up.
    useEffect(() => {
      return () => {
        document.removeEventListener('click', handleCloseToolPanel);
        window.removeEventListener('scroll', handleAlignToolPanelToSettingsButton);
        window.removeEventListener('resize', handleAlignToolPanelToSettingsButton);
      };
    }, [handleCloseToolPanel, handleAlignToolPanelToSettingsButton]);

    // Whenever the grid changes, check the size of the user defined columns and compare it to the size of the grid.
    // If there is space available, add a dummy column to fill the remaining space.
    useLayoutEffect(() => {
      const gridApi = agGridApi.current;
      const mainGridWrapper = document.querySelector(AG_GRID_MAIN_WRAPPER_CLASSNAME);

      if (gridApi && mainGridWrapper && enableDummyColumn) {
        const determineNeedForDummyColumn = () => {
          const gridWidth = mainGridWrapper.clientWidth;
          const allDisplayedColumns = gridApi.getAllDisplayedColumns();
          const definedColumns = allDisplayedColumns.filter((column) => column.getColId() !== DUMMY_COLUMN_NAME);
          const combinedSizeOfDefinedColumns = definedColumns.reduce((prev, next) => prev + next.getActualWidth(), 0);

          setNeedsDummyColumn(combinedSizeOfDefinedColumns < gridWidth);
        };

        gridApi.addGlobalListener(determineNeedForDummyColumn);

        return () => {
          gridApi.removeGlobalListener(determineNeedForDummyColumn);
        };
      }
    });

    // Adding divider between selectable and next column
    useLayoutEffect(() => {
      const leftPinnedHeader = document.querySelector(AG_GRID_PINNED_LEFT_HEADER);
      if (leftPinnedHeader) {
        const divider = document.createElement('div');
        divider.style.width = '1px';
        divider.style.height = '50%';
        divider.style.top = '0px';
        divider.style.right = '0px';
        divider.style.position = 'absolute';
        divider.style.backgroundColor = '#d9e0e5';

        leftPinnedHeader.appendChild(divider);

        return () => {
          leftPinnedHeader.removeChild(divider);
        };
      }
    });

    if (inEmptyState && EmptySetComponent === undefined) {
      return <EmptySetOverlay {...emptySetProps} />;
    }

    return (
      <DataCard
        ref={wrapperRef}
        className={className}
        heading={title || 'Results'}
        description={subheader}
        actions={
          !isPrintable && (
            <Box display="flex" alignItems="center">
              {headerActions}
              {includeSettingsHeaderAction && (
                <Tooltip title="Settings" arrow={true} placement="top">
                  <IconButton
                    ref={iconButtonRef}
                    id="column-settings"
                    size="medium"
                    onClick={handleColumnSettings}
                    style={{ position: 'relative' }}
                  >
                    <SettingsOutlined />
                  </IconButton>
                </Tooltip>
              )}
              {includeExportHeaderAction && <ClientSideDataGridExport exportToXlsx={exportToXlsx} />}
              {includeSearch && !isServerSideGrid && <ClientSideDataGridSearch onQueryChange={setSearchQuery} />}
              {includeSearch && isServerSideGrid && (
                <ServerSideDataGridSearch onQueryChange={handleServerSideQuickFilterChange} />
              )}
              {includeTableFilters && (
                <Button
                  color="primary"
                  data-testid="table-filters"
                  size="small"
                  variant="text"
                  onClick={handleFilterSettings}
                >
                  Table Filters ({filterCount})
                </Button>
              )}
              {includeMenuHeaderAction && (
                <DataGrableHeaderMenuActions
                  actions={menuHeaderActions.actions}
                  disabled={menuHeaderActions.disabled}
                />
              )}
            </Box>
          )
        }
      >
        <div style={{ visibility: loadedFromPersistence ? 'visible' : 'hidden' }}>
          {inEmptyState && EmptySetComponent ? (
            <>
              <Divider />
              <EmptySetComponent {...emptySetProps} />
            </>
          ) : (
            <>
              <WrappedComponent
                {...rest}
                gridDefaultDimensions={{ ...gridDefaultDimensions, height: `${calculatedGridHeightRef.current}px` }}
                gridApiRef={agGridApi}
                gridConfig={fullGridConfig}
                loading={loading}
                onGridReady={(e: GridReadyEvent) => {
                  loadPersistedState();
                  getApiReference?.(e.api);
                  if (isPrintable) e?.api?.sizeColumnsToFit();

                  props.onGridReady?.(e);

                  adjustGridHeight();
                }}
                onColumnVisible={(e: ColumnVisibleEvent) => {
                  props.onColumnVisible?.(e);
                }}
                onFilterChanged={(e: FilterChangedEvent) => {
                  if (includeTableFilters) {
                    const allFilters: FilterModel = e.api.getFilterModel();
                    const filterCount: number = Object.keys(allFilters).length;
                    setFilterCount(filterCount);
                  }

                  handleFilterChanged();
                  props.onFilterChanged?.(e);
                }}
                onRowClicked={loading ? undefined : onRowClicked}
              />
              {FooterComponent ? <div>{FooterComponent}</div> : null}
            </>
          )}
        </div>
      </DataCard>
    );
  }
  return memo(EnhancedComponent);
}

function formatPersistenceIdentifier({ namespace, gridId, userId = '<NONE>' }: DataGrablePersistenceSettings) {
  return `[ns=${namespace}][g=${gridId}][u=${userId}]`;
}
